"use client";

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  ScrollArea,
  Spinner,
  Text,
  TextArea,
} from "@radix-ui/themes";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import CreatorResults, {
  type CreatorToolResult,
} from "@/components/Campaign/CreatorResults";
import {
  getCampaignAssistantHistory,
  streamCampaignAssistant,
  type AssistantHistoryTurn,
  type AssistantToolCallTrace,
} from "@/services/assistant";
import useUser from "@/lib/useUser";
import { MAIN_AGENCY_ID } from "@/lib/constants";
const SUGGESTED_QUESTIONS = [
  "Why did total views change recently?",
  "Were any posts removed from this campaign?",
  "Which posts drive most of the views?",
  "How many posts went up in the last 7 days?",
  "Are any posts missing or showing view drops?",
];

const CREATOR_RESULT_TOOLS = new Set([
  "recommend_creators",
  "discover_creators",
]);

const TOOL_PROGRESS_LABELS: Record<string, string> = {
  discover_creators: "Searching live social content for creators...",
  recommend_creators: "Ranking roster creators...",
  list_creator_tags: "Reading creator tags...",
  campaign_budget: "Loading budget data...",
  top_posts: "Finding top posts...",
  creator_post_performance: "Analyzing creator performance...",
  scrape_schedule: "Checking scrape schedule...",
  query_schema: "Loading data schema...",
  query_campaign_data: "Querying campaign data...",
  list_accessible_campaigns: "Comparing across campaigns...",
};

function toolProgressLabel(toolCalls?: AssistantToolCallTrace[]): string {
  if (!toolCalls?.length) return "Analyzing campaign data...";
  const last = toolCalls[toolCalls.length - 1];
  return TOOL_PROGRESS_LABELS[last.name] ?? "Analyzing campaign data...";
}

function isCreatorToolResult(
  name: string,
  result: unknown,
): result is CreatorToolResult {
  if (!CREATOR_RESULT_TOOLS.has(name) || !result || typeof result !== "object") {
    return false;
  }
  const r = result as CreatorToolResult;
  return Array.isArray(r.creators) || !!r.error || !!r.message;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  toolCalls?: AssistantToolCallTrace[];
  createdAt?: string;
}

interface CampaignAssistantProps {
  campaignId: number;
}

function formatMessageTime(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const sameDay = date.toDateString() === new Date().toDateString();
  return date.toLocaleString(
    undefined,
    sameDay
      ? { hour: "numeric", minute: "2-digit" }
      : { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" },
  );
}

function AssistantLoadingIndicator({
  label,
}: {
  label: string;
}) {
  return (
    <Flex align="center" gap="2" className="assistant-loading">
      <Spinner size="1" />
      <Text size="2" color="gray">
        {label}
      </Text>
    </Flex>
  );
}

function AssistantMarkdown({ content }: { content: string }) {
  return (
    <Box className="assistant-markdown" style={{ fontSize: "var(--font-size-2)" }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node, ...props }) => (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--accent-11)", wordBreak: "break-all" }}
            />
          ),
          p: ({ node, ...props }) => (
            <p {...props} style={{ margin: "0 0 8px" }} />
          ),
          ul: ({ node, ...props }) => (
            <ul {...props} style={{ margin: "0 0 8px", paddingLeft: 18 }} />
          ),
          ol: ({ node, ...props }) => (
            <ol {...props} style={{ margin: "0 0 8px", paddingLeft: 18 }} />
          ),
          li: ({ node, ...props }) => (
            <li {...props} style={{ margin: "2px 0" }} />
          ),
          table: ({ node, ...props }) => (
            <div className="assistant-table-wrap">
              <table {...props} />
            </div>
          ),
          code: ({ node, ...props }) => (
            <code
              {...props}
              style={{
                background: "var(--gray-4)",
                borderRadius: 4,
                padding: "1px 4px",
                fontSize: "0.9em",
              }}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}

export default function CampaignAssistant({ campaignId }: CampaignAssistantProps) {
  const { checkRoles, user } = useUser();
  const isSuperAdmin = checkRoles(["superadmin"]);
  const isAdmin = checkRoles(["agency-admin", "superadmin"]);
  const isMainAgency = user?.agencyId === MAIN_AGENCY_ID;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedTools, setExpandedTools] = useState<Record<number, boolean>>({});
  const [size, setSize] = useState({ width: 440, height: 600 });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });
  const loadedForCampaign = useRef<number | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  // Stick to the bottom unless the user has scrolled up to read earlier messages.
  const stickToBottom = useRef(true);

  const getViewport = () => {
    const node = scrollAreaRef.current;
    if (!node) return null;
    // Radix Themes forwards the ScrollArea ref to the viewport element itself.
    if (node.matches?.("[data-radix-scroll-area-viewport]")) return node;
    return node.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
  };

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    const viewport = getViewport();
    if (!viewport) return;
    viewport.scrollTo({ top: viewport.scrollHeight, behavior });
  };

  const handleViewportScroll = () => {
    const viewport = getViewport();
    if (!viewport) return;
    const distanceFromBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    stickToBottom.current = distanceFromBottom < 80;
  };

  useEffect(() => {
    if (loadedForCampaign.current !== null && loadedForCampaign.current !== campaignId) {
      loadedForCampaign.current = null;
      setMessages([]);
    }
  }, [campaignId]);

  useEffect(() => {
    if (!open || !isMainAgency || !isAdmin) return;
    if (loadedForCampaign.current === campaignId) return;

    let cancelled = false;
    setHistoryLoading(true);

    getCampaignAssistantHistory(campaignId)
      .then((rows) => {
        if (cancelled) return;
        setMessages(
          rows.map((row) => ({
            role: row.role,
            content: row.content,
            toolCalls: row.toolCalls,
            createdAt: row.createdAt,
          })),
        );
        loadedForCampaign.current = campaignId;
      })
      .catch(() => {
        if (!cancelled) loadedForCampaign.current = campaignId;
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, campaignId, isMainAgency, isAdmin]);

  useEffect(() => {
    if (stickToBottom.current) scrollToBottom("auto");
  }, [messages]);

  useEffect(() => {
    if (open) {
      stickToBottom.current = true;
      // Wait for the panel/messages to render before jumping to the bottom.
      requestAnimationFrame(() => scrollToBottom("auto"));
    }
  }, [open]);

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: PointerEvent) => {
      const { x, y, w, h } = resizeStart.current;
      const maxW = window.innerWidth - 48;
      const maxH = window.innerHeight - 120;
      setSize({
        width: Math.min(maxW, Math.max(320, w + (x - e.clientX))),
        height: Math.min(maxH, Math.max(360, h + (y - e.clientY))),
      });
    };
    const onUp = () => setIsResizing(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [isResizing]);

  const sendQuestion = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || loading || historyLoading) return;

    setError(null);
    setLoading(true);
    setInput("");
    // A fresh submission always pins the view to the latest messages.
    stickToBottom.current = true;

    const history: AssistantHistoryTurn[] = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    const now = new Date().toISOString();
    setMessages((prev) => [
      ...prev,
      { role: "user", content: trimmed, createdAt: now },
      { role: "assistant", content: "", toolCalls: [], createdAt: now },
    ]);

    const updateAssistant = (
      updater: (msg: ChatMessage) => ChatMessage,
    ) => {
      setMessages((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === "assistant") {
            next[i] = updater(next[i]);
            break;
          }
        }
        return next;
      });
    };

    try {
      const result = await streamCampaignAssistant(
        campaignId,
        trimmed,
        history,
        {
          onDelta: (text) =>
            updateAssistant((m) => ({ ...m, content: m.content + text })),
          onReset: () => updateAssistant((m) => ({ ...m, content: "" })),
          onTool: (trace) =>
            updateAssistant((m) => ({
              ...m,
              toolCalls: [...(m.toolCalls ?? []), trace],
            })),
        },
      );
      updateAssistant((m) => ({
        ...m,
        content: result.answer,
        toolCalls: result.toolCalls,
        createdAt: new Date().toISOString(),
      }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to get assistant response";
      setError(message);
      setMessages((prev) => prev.slice(0, -2));
      setInput(trimmed);
    } finally {
      setLoading(false);
    }
  };

  const toggleTools = (index: number) => {
    setExpandedTools((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  if (!isMainAgency || !isAdmin) return null;

  return (
    <Box
      style={{
        position: "fixed",
        right: 24,
        bottom: 24,
        zIndex: 50,
        width: "auto",
      }}
    >
      {open && (
        <Card
          size="2"
          style={{
            marginBottom: 12,
            position: "relative",
            width: size.width,
            height: size.height,
            maxWidth: "calc(100vw - 48px)",
            maxHeight: "calc(100vh - 120px)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "var(--shadow-5)",
            userSelect: isResizing ? "none" : undefined,
          }}
        >
          <Box
            onPointerDown={(e) => {
              e.preventDefault();
              resizeStart.current = {
                x: e.clientX,
                y: e.clientY,
                w: size.width,
                h: size.height,
              };
              setIsResizing(true);
            }}
            title="Drag to resize"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 18,
              height: 18,
              cursor: "nwse-resize",
              zIndex: 3,
              borderTop: "2px solid var(--gray-7)",
              borderLeft: "2px solid var(--gray-7)",
              borderTopLeftRadius: "var(--radius-3)",
            }}
          />
          <Flex justify="between" align="center" mb="2">
            <Text size="3" weight="bold">
              Campaign Assistant
            </Text>
            <IconButton
              variant="ghost"
              color="gray"
              size="1"
              onClick={() => setOpen(false)}
              aria-label="Close assistant"
            >
              ✕
            </IconButton>
          </Flex>

          <Text size="1" color="gray" mb="2">
            Ask about views, removals, and post performance. Answers use live
            campaign data.
          </Text>

          {historyLoading && (
            <Text size="2" color="gray" mb="2">
              Loading conversation...
            </Text>
          )}

          {messages.length === 0 && !historyLoading && (
            <Box mb="3">
              <Text
                size="1"
                color="gray"
                weight="medium"
                mb="2"
                as="p"
                style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
              >
                Try asking
              </Text>
              <Flex direction="column" gap="2">
                {SUGGESTED_QUESTIONS.map((q) => (
                  <Button
                    key={q}
                    size="2"
                    variant="soft"
                    color="gray"
                    disabled={loading || historyLoading}
                    onClick={() => sendQuestion(q)}
                    style={{
                      width: "100%",
                      height: "auto",
                      padding: "10px 12px",
                      justifyContent: "space-between",
                      whiteSpace: "normal",
                      textAlign: "left",
                      lineHeight: 1.3,
                      cursor: "pointer",
                    }}
                  >
                    <span>{q}</span>
                    <span style={{ opacity: 0.5, flexShrink: 0 }}>→</span>
                  </Button>
                ))}
              </Flex>
            </Box>
          )}

          <ScrollArea
            ref={scrollAreaRef}
            style={{ flex: 1 }}
            scrollbars="vertical"
            onScroll={handleViewportScroll}
          >
            <Flex direction="column" gap="3" pr="2">
              {messages.map((msg, idx) => (
                <Box key={idx}>
                  <Card
                    variant="surface"
                    style={{
                      background:
                        msg.role === "user"
                          ? "var(--accent-3)"
                          : "var(--gray-2)",
                    }}
                  >
                    <Flex justify="between" align="center" gap="2" mb="1">
                      <Text size="1" weight="bold" as="span">
                        {msg.role === "user" ? "You" : "Assistant"}
                      </Text>
                      {formatMessageTime(msg.createdAt) && (
                        <Text
                          size="1"
                          color="gray"
                          as="span"
                          title={
                            msg.createdAt
                              ? new Date(msg.createdAt).toLocaleString()
                              : undefined
                          }
                        >
                          {formatMessageTime(msg.createdAt)}
                        </Text>
                      )}
                    </Flex>
                    {msg.role === "assistant" && msg.content ? (
                      <AssistantMarkdown content={msg.content} />
                    ) : msg.role === "assistant" && loading && !msg.content ? (
                      <AssistantLoadingIndicator
                        label={toolProgressLabel(msg.toolCalls)}
                      />
                    ) : (
                      <Text size="2" style={{ whiteSpace: "pre-wrap" }}>
                        {msg.content}
                      </Text>
                    )}
                    {msg.role === "assistant" &&
                      msg.toolCalls?.map((trace, toolIdx) => {
                        if (
                          !CREATOR_RESULT_TOOLS.has(trace.name) ||
                          !isCreatorToolResult(trace.name, trace.result)
                        ) {
                          return null;
                        }
                        const result = trace.result as CreatorToolResult;
                        const hasCreators = (result.creators?.length ?? 0) > 0;
                        const hasNotice = !!result.error || !!result.message;
                        if (!hasCreators && !hasNotice) return null;
                        return (
                          <CreatorResults
                            key={`${idx}-tool-${toolIdx}-${trace.name}`}
                            toolName={
                              trace.name as
                                | "recommend_creators"
                                | "discover_creators"
                            }
                            result={result}
                          />
                        );
                      })}
                  </Card>
                  {isSuperAdmin && msg.role === "assistant" && msg.toolCalls && msg.toolCalls.length > 0 && (
                    <Box mt="1">
                      <Button
                        size="1"
                        variant="ghost"
                        color="gray"
                        onClick={() => toggleTools(idx)}
                      >
                        {expandedTools[idx] ? "Hide" : "Show"} tools used (
                        {msg.toolCalls.length})
                      </Button>
                      {expandedTools[idx] && (
                        <Card variant="ghost" mt="1">
                          <Text
                            size="1"
                            style={{
                              fontFamily: "monospace",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {JSON.stringify(msg.toolCalls, null, 2)}
                          </Text>
                        </Card>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
              {error && (
                <Text size="2" color="red">
                  {error}
                </Text>
              )}
            </Flex>
          </ScrollArea>

          <Flex gap="2" mt="3" align="end">
            <TextArea
              placeholder="Ask a question about this campaign..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendQuestion(input);
                }
              }}
              disabled={loading || historyLoading}
              rows={1}
              style={{ flex: 1 }}
              className="assistant-input"
            />
            <Button
              size="3"
              onClick={() => sendQuestion(input)}
              disabled={loading || historyLoading || !input.trim()}
              style={{ cursor: "pointer", flexShrink: 0 }}
            >
              Send
            </Button>
          </Flex>
        </Card>
      )}

      <Flex justify="end">
        <Button
          size="3"
          variant="solid"
          color="blue"
          onClick={() => setOpen((v) => !v)}
          style={{ borderRadius: "999px", boxShadow: "var(--shadow-4)" }}
        >
          {open ? "Close AI" : "Ask AI"}
        </Button>
      </Flex>
    </Box>
  );
}
